import { useState, type JSX } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { PRICING, displayPrice, hasAnnual, type BillingInterval } from '@yuan-math/config';
import type { Tier } from '@yuan-math/learning';
import { Card } from '@yuan-math/ui';
import { useAuth } from '../state/AuthContext.tsx';
import { useLearner } from '../state/LearnerContext.tsx';
import { startCheckout } from '../state/billing.ts';

/** Pricing (§28). Free is a real tier, not a trial. */
export function PricingPage(): JSX.Element {
  const tiers = [PRICING.free, PRICING.premium, PRICING.family, PRICING.school];
  const { signedIn, account } = useAuth();
  const { entitlements } = useLearner();
  const [params] = useSearchParams();
  const [busy, setBusy] = useState<Tier | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [interval, setInterval] = useState<BillingInterval>('month');

  const cancelled = params.get('checkout') === 'cancelled';

  /** Sends the tier and interval, never a price — the amount is set server-side. */
  const subscribe = async (tier: Tier): Promise<void> => {
    setError(null);
    setBusy(tier);
    const result = await startCheckout(tier, interval);
    if ('url' in result) {
      window.location.href = result.url;
      return;
    }
    setError(result.error);
    setBusy(null);
  };

  const tierOf = (name: string): Tier | null =>
    name === 'Premium' ? 'premium' : name === 'Family' ? 'family' : null;

  return (
    <div className="page page--wide">
      <header className="page-header page-header--centred">
        <h1 className="page__title">Pricing</h1>
        <p className="page__lead">
          The free tier is not a trial. A student who never pays can still learn every published lesson, use the full
          Math Map, and track real mastery.
        </p>
      </header>

      <IntervalToggle interval={interval} onChange={setInterval} />

      <div className="pricing-grid">
        {tiers.map((tier) => {
          const shown = displayPrice(tier, interval);

          return (
          <Card key={tier.name} as="article" className={`pricing-card ${'highlighted' in tier && tier.highlighted ? 'pricing-card--featured' : ''}`}>
            <h2 className="pricing-card__name">{tier.name}</h2>
            <p className="pricing-card__price">
              <span>{shown.price}</span>
              <span className="pricing-card__period">{shown.period}</span>
            </p>
            {shown.monthly ? (
              <p className="pricing-card__annual">
                {shown.monthly} a month, billed yearly — {shown.saving}
              </p>
            ) : null}
            {interval === 'year' && !hasAnnual(tier) ? (
              // Said plainly rather than left blank: this plan is simply not
              // billed by the year, and the price shown is the real one.
              <p className="pricing-card__annual">Not billed yearly</p>
            ) : null}
            <p className="pricing-card__description">{tier.description}</p>
            <ul className="pricing-card__features">
              {tier.features.map((feature) => (
                <li key={feature}>{feature}</li>
              ))}
            </ul>
            <TierAction
              name={tier.name}
              cta={tier.cta}
              featured={'highlighted' in tier && Boolean(tier.highlighted)}
              tier={tierOf(tier.name)}
              interval={interval}
              signedIn={signedIn}
              isChild={account?.kind === 'child'}
              current={entitlements.tier}
              busy={busy}
              onSubscribe={subscribe}
            />
          </Card>
          );
        })}
      </div>

      {cancelled ? (
        <p className="page__note" role="status">
          Checkout was cancelled — nothing has been charged, and nothing has changed.
        </p>
      ) : null}
      {error ? <p className="form-error" role="alert">{error}</p> : null}

      <section className="faq">
        <h2>Straight answers</h2>
        <dl>
          <dt>Is the free tier crippled?</dt>
          <dd>
            No. Every published lesson, the whole Math Map, mastery tracking and a daily challenge are free. The cap
            is on volume of practice, not on what you are allowed to learn.
          </dd>
          <dt>Do you advertise to children?</dt>
          <dd>Never. There is no advertising anywhere in the product, on any tier.</dd>
          <dt>What happens to the data if we stop paying?</dt>
          <dd>Progress stays, and can be exported at any time from the parent view.</dd>
          <dt>Where is the AI tutor?</dt>
          <dd>
            Not shipped. The deterministic system — the concept graph, mastery, diagnosis of misconceptions — has to
            work first. A chatbot bolted onto worksheets is not tutoring.
          </dd>
        </dl>
      </section>
    </div>
  );
}

/**
 * Monthly or yearly.
 *
 * A radio group rather than a switch, because a switch has no name for the
 * state it is *not* in — someone using a screen reader should hear both prices
 * offered, not "billing toggle, off".
 */
function IntervalToggle({
  interval,
  onChange,
}: {
  interval: BillingInterval;
  onChange(next: BillingInterval): void;
}): JSX.Element {
  return (
    <fieldset className="interval-toggle">
      <legend className="interval-toggle__legend">How often you are billed</legend>
      {(['month', 'year'] as const).map((option) => (
        <label
          key={option}
          className={`interval-toggle__option ${interval === option ? 'interval-toggle__option--on' : ''}`}
        >
          <input
            type="radio"
            name="billing-interval"
            value={option}
            checked={interval === option}
            onChange={() => onChange(option)}
          />
          {option === 'month' ? 'Monthly' : 'Yearly'}
          {option === 'year' ? <span className="interval-toggle__saving">2 months free</span> : null}
        </label>
      ))}
    </fieldset>
  );
}

/**
 * The button under each plan.
 *
 * Four different states, and each says something true: the plan you already
 * have is not sellable to you, a child account cannot enter card details
 * (§29), a signed-out visitor needs an account first, and everything else is a
 * checkout.
 */
function TierAction({
  name,
  cta,
  featured,
  tier,
  interval,
  signedIn,
  isChild,
  current,
  busy,
  onSubscribe,
}: {
  name: string;
  cta: string;
  featured: boolean;
  tier: Tier | null;
  interval: BillingInterval;
  signedIn: boolean;
  isChild: boolean;
  current: Tier;
  busy: Tier | null;
  onSubscribe(tier: Tier): Promise<void>;
}): JSX.Element {
  const variant = featured ? 'primary' : 'secondary';

  if (tier && current === tier) {
    return <p className="pricing-card__current">Your current plan</p>;
  }

  if (name === 'School') {
    return (
      <a href="mailto:hello@yuan-math.com?subject=School%20plan" className={`btn btn--${variant} btn--full`}>
        {cta}
      </a>
    );
  }

  if (!tier) {
    return (
      <Link to="/start" className={`btn btn--${variant} btn--full`}>
        {cta}
      </Link>
    );
  }

  if (!signedIn) {
    return (
      <Link to={`/register?plan=${tier}&billing=${interval}`} className={`btn btn--${variant} btn--full`}>
        {cta}
      </Link>
    );
  }

  if (isChild) {
    // Money is an adult's decision. The Edge Function refuses this too, so the
    // rule holds even if someone calls the API directly.
    return <p className="pricing-card__current">A parent or guardian sets this up</p>;
  }

  return (
    <button
      type="button"
      className={`btn btn--${variant} btn--full`}
      disabled={busy !== null}
      onClick={() => void onSubscribe(tier)}
    >
      {busy === tier ? 'Opening checkout…' : cta}
    </button>
  );
}
